
/* injects from baggage-loader */
require('./style.styl');

import React from 'react'
import PropTypes from 'prop-types'
import Legend from 'components/Legend'
import Label from 'components/Label'
import {Table, Column, Cell} from 'components/Table'
import Icon from 'components/Icon'
import Container from 'components/ContainerDimentionsContext'
import OverviewTableChart from './OverviewTableChart'
import ChannelCell from './ChannelCell'
import TimePeriodSelector from 'components/TimePeriodSelector'
import Select from 'components/Select'
import partial from 'utils/partial'
import classnames from 'classnames'
import isWithinRange from 'date-fns/is_within_range'
import formatTimePeriodDate from 'utils/formatTimePeriodDate'

export default class OverviewTable extends React.Component {
  static propTypes = {
    className: PropTypes.string,
    timePeriod: PropTypes.string,
    timePeriodRanges: PropTypes.object,
    data: PropTypes.array,
    isLoading: PropTypes.bool,
    sortKey: PropTypes.string,
    sortDirection: PropTypes.string,
    setTimePeriod: PropTypes.func,
    dynamicDateType: PropTypes.string,
    changeSort: PropTypes.func,
    disabled: PropTypes.bool,
    invertAreaColors: PropTypes.bool,
    today: PropTypes.instanceOf(Date),
    columns: PropTypes.array,
    selectedMetric: PropTypes.string,
    metrics: PropTypes.object,
    onMetricChange: PropTypes.func,
    conversionType: PropTypes.string,
    setConversionType: PropTypes.func
  }

  conversions = [
    {
      name: 'Registration Finished',
      value: 'Reg finished'
    },
    {
      name: 'First Time Deposit',
      value: 'FTD'
    }
  ]

  render() {
    const {className} = this.props
    const classNames = classnames('overview-table', className)

    return (
      <div className={classNames}>
        {this.renderPeriod()}
        {this.renderTable()}
      </div>
    )
  }

  renderPeriod() {
    const {
      timePeriodRanges,
      timePeriod
    } = this.props

    const currentPeriodName = formatTimePeriodDate(timePeriod, timePeriodRanges.start.start)
    const anotherPeriodName = formatTimePeriodDate(timePeriod, timePeriodRanges.end.start)
    const timePeriodText = (timePeriod == 'range') ? 'Dai' : timePeriod
    return (
      <div className='overview-table_header'>
        <div className='overview-table_header_title'>
          {timePeriodText}ly comparison
        </div>
        <div className='overview-table_header_body'>
          {this.renderConversion()}
          {this.renderTimePeriod()}
          {this.renderMetricSelect()}
        </div>
        <Legend className='overview-table_header_legend' align='right' inline>
          <Label color='awesome-blue' line>{currentPeriodName}</Label>
          <Label color='lightest' line>{anotherPeriodName}</Label>
          <Label color='awesome-blue' line dashed>Projection</Label>
        </Legend>
      </div>
    )
  }

  renderConversion() {
    const {conversionType, setConversionType} = this.props
    if (!conversionType || !setConversionType) {
      return null
    }

    return (
      <Select
        className='overview-table_select_conversion'
        options={this.conversions}
        value={conversionType}
        onSelect={(item) => setConversionType(item.value)}
      />
    )
  }

  renderTimePeriod() {
    const {timePeriod, timePeriodRanges, dynamicDateType, disabled, today} = this.props
    return (
      <TimePeriodSelector
        className='dashboard_charts-header_period'
        timePeriod={timePeriod}
        timeRanges={timePeriodRanges}
        dynamicDateType={dynamicDateType}
        disabled={disabled}
        today={today}
        onSelect={this.onSelectPeriod}
      />
    )
  }

  renderMetricSelect() {
    const {selectedMetric, metrics, onMetricChange, disabled} = this.props
    if (!selectedMetric || !metrics || !onMetricChange) return null

    return (
      <div className='overview-table_metrics'>
        <div className='overview-table_metrics_title'>using</div>
        <Select
          className='overview-table_select'
          value={selectedMetric}
          disabled={disabled}
          options={Object.keys(metrics).map(metric => ({
            name: metrics[metric].model,
            value: metric
          }))}
          onSelect={onMetricChange}
        />
        <div className='overview-table_metrics_icon'>
          <Icon
            tooltipPlace='bottom'
            tooltipAlign='end'
            tooltipStyle='default'
            tooltip={(<div className='overview-table_metrics_tooltip'>
              {Object.keys(metrics).map((metric, index) => {
                const {model, description} = metrics[metric]
                return (
                  <p key={index}><strong>{model}</strong> &mdash; {description}</p>
                )
              })}
            </div>)}
            name='tooltip-question'
            size='20px'
          />
        </div>
      </div>
    )
  }

  renderTable() {
    const {data, isLoading, timePeriod} = this.props

    return (
      <Table
        className='overview-table_table'
        data={data}
        loading={isLoading}
        empty={<Cell>We don{`'`}t have any data for the current {timePeriod} yet.</Cell>}
      >
        <Column
          header='Channel'
          cell={this.renderChannelDetails}
          width='12%'
        />
        {this.renderCharts()}
        {this.renderColumns()}
      </Table>
    )
  }

  renderTableHeader(key, label, description) {
    return (
      <Cell onClick={partial(this.onSort, key)}>
        <div className='overview-table_table_header'>
          <div className='overview-table_table_header_wrapper'>
            {this.renderSortIcon(key)}
            <span className='overview-table_table_header_item'>
              {label}
            </span>
          </div>
          <span className='overview-table_table_header_description'>
            {description}
          </span>
        </div>
      </Cell>
    )
  }

  renderCharts() {
    const {columns} = this.props
    return columns.filter(column => column.type === 'chart').map((column, index, arr) => {
      const {
        label,
        width
      } = column

      return (
        <Column
          key={index}
          header={
            (arr.length === 1)
              ? label
              : <Cell className='overview-table_table_header-chart'>
                <div className='overview-table_table_header-label'>{label}</div>
              </Cell>
          }
          cell={data => this.renderChart(column, data)}
          width={width !== 'grow' ? width : null}
          grow={width === 'grow'}
        />
      )
    })
  }

  renderColumns() {
    const {columns} = this.props
    return columns.filter(column => column.type === 'value').map((column, index) => {
      const {
        headerKey,
        label,
        description,
        width,
        dataKey,
        format,
        applyColor
      } = column

      return (
        <Column
          key={index}
          header={this.renderTableHeader(headerKey, label, description)}
          cell={partial(this.renderCell, dataKey, format, applyColor)}
          width={width}
        />
      )
    })
  }

  renderCell(key, format, applyColor, data) {
    const value = data[key]
    const classNames = classnames('overview-table_table_cell', {
      'is-negative': (value < 0) && applyColor,
      'is-positive': (value > 0) && applyColor,
      'is-huge': (value < 1e5),
      'is-big': (value > 1e5 && value < 1e6),
      'is-normal': (value > 1e6)
    })

    return (
      <Cell className={classNames} format={format} emptyPlaceholder={'-'}>
        {value}
      </Cell>
    )
  }

  renderSortIcon(key) {
    const {sortKey, sortDirection} = this.props

    const classNames = classnames('overview-table_table_header_icon', {
      'is-asc': (sortDirection === 'asc')
    })

    return (
      <span className={classNames}>
        {(sortKey === key) ? <Icon name='chevron' size='10px' /> : null}
      </span>
    )
  }

  renderChannelDetails(data) {
    const {channelId, channelGroupId} = data

    return (
      <Cell>
        <ChannelCell channelGroupId={channelGroupId} channelId={channelId} />
      </Cell>
    )
  }

  renderChart(column, data) {
    const {invertAreaColors} = this.props
    const {width, dataKey, accumulatedValues, format} = column
    const {chartData} = data
    const isGrow = width === 'grow'
    const classNames = classnames({
      'overview-table_table_double-chart': !isGrow,
      'overview-table_table_chart': isGrow
    })

    return (
      <Cell className={classNames}>
        <Container className='overview-table_chart'>
          <OverviewTableChart
            data={chartData}
            today={this.getToday()}
            dataKey={dataKey}
            format={format}
            accumulatedValues={accumulatedValues}
            invertAreaColors={invertAreaColors}
          />
        </Container>
      </Cell>
    )
  }

  onSelectPeriod = (timePeriod, startRange, endRange, dynamicDateType) => {
    const {setTimePeriod} = this.props
    if (!timePeriod) return null

    setTimePeriod({
      timePeriod,
      startRange,
      endRange,
      dynamicDateType
    })
  }

  onSort = newSortKey => {
    const {changeSort, sortKey, sortDirection} = this.props
    if (newSortKey === sortKey) {
      changeSort({
        sortKey,
        sortDirection: (sortDirection === 'desc') ? 'asc' : 'desc'
      })
    } else {
      changeSort({
        sortKey: newSortKey,
        sortDirection: 'desc'
      })
    }
  }

  getToday() {
    const {today, timePeriodRanges} = this.props
    const {start: startRange, end: endRange} = timePeriodRanges
    const isTodayWithinRanges = isWithinRange(today, startRange.start, startRange.end) ||
      isWithinRange(today, endRange.start, endRange.end)
    return isTodayWithinRanges ? today : null
  }
}



// WEBPACK FOOTER //
// ./client/components/OverviewTable/index.js